import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Phone, Radar, Users, Scale, BookOpen, MessageCircle } from 'lucide-react';
import ImmobilienCard from '../../../components/ImmobilienCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { ANDREAS_PORTRAIT, AERIAL_LANDSCAPE } from '../../../data/brandAssets';
import { homeFaq } from '../../../data/homeContent';
import { trackEvent, analyticsEvents, contactInfo } from '../../../data/mock';
import { SectionHeading } from './CoreSections';

const API = process.env.REACT_APP_BACKEND_URL;

export const ListingsSection = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/properties?limit=3`).then((r) => (r.ok ? r.json() : { properties: [] }))
      .then((d) => setItems((d.properties || d || []).slice(0, 3))).catch(() => setItems([])).finally(() => setLoaded(true));
  }, []);
  if (loaded && items.length === 0) return null;
  return (
    <section className="hv-listings bg-[#F8F6F0] py-24 lg:py-32" data-testid="homepage-listings">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <SectionHeading kicker="Aktuell in der Vermarktung" title="Flächen, die wir gerade anbieten." text="Ein Ausschnitt. Weitere Objekte vermarkten wir diskret ohne Inserat." />
          <Link to="/immobilien" data-testid="listings-all-link" className="hv-link inline-flex items-center gap-2 text-[#19392B] font-medium underline underline-offset-8 decoration-[#DFC590] mb-14">Alle Immobilien <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((im) => <ImmobilienCard key={im.id} immobilie={im} compact />)}
        </div>
      </div>
    </section>
  );
};

export const AndreasHeinrichsSection = () => (
  <section id="andreas-section" className="hv-andreas bg-[#EEE9DC] py-24 lg:py-32" data-testid="homepage-andreas">
    <div className="container mx-auto px-4">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="hv-portrait relative aspect-[4/5] max-w-[480px] overflow-hidden rounded-[4px] shadow-[0_30px_60px_-30px_rgba(21,59,43,.5)]">
            <img src={ANDREAS_PORTRAIT} data-testid="homepage-andreas-portrait" alt="Andreas Heinrichs, Geschäftsführer AgrarKapital" loading="lazy" className="w-full h-full object-cover object-[50%_40%]" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <SectionHeading kicker="Ihr Ansprechpartner" title="Ich sage Ihnen auch, wenn Sie nicht verkaufen sollten." />
          <blockquote className="hv-serif text-2xl lg:text-[28px] leading-snug text-[#19392B] mb-8 max-w-[36ch] border-l-2 border-[#DFC590] pl-6">
            „Wer Acker, Wald oder Wiesen verkauft, will keine Floskeln hören. Sie bekommen von mir Zahlen, echte Käufer und eine klare Empfehlung – auch wenn sie lautet: behalten.“
          </blockquote>
          <p className="text-[#647064] leading-relaxed mb-10 max-w-[60ch]">
            Andreas Heinrichs, Geschäftsführer und Fachmakler. Spezialisiert ausschließlich auf Agrarflächen, zu Hause in Freising, unterwegs in ganz Deutschland. Sie sprechen von Anfang bis Notar mit ihm – nicht mit einem Callcenter.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a href={contactInfo.phoneLink} onClick={() => trackEvent(analyticsEvents.PHONE_CLICK, { location: 'andreas' })} data-testid="andreas-phone" className="hv-btn-primary inline-flex items-center gap-2 h-[56px] px-7 rounded-[4px] bg-[#1C432F] text-[#F8F6F0] font-semibold hover:bg-[#153B2B]"><Phone className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />{contactInfo.phone}</a>
            <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: 'whatsapp', location: 'andreas' })} data-testid="andreas-whatsapp" className="hv-btn-secondary inline-flex items-center gap-2 h-[56px] px-6 rounded-[4px] border border-[#1C432F] text-[#19392B] font-semibold hover:bg-[#FFFEFA]"><MessageCircle className="w-4 h-4" aria-hidden="true" />WhatsApp</a>
            <Link to="/bewertung" data-testid="andreas-cta" className="hv-link text-[#19392B] font-medium underline underline-offset-8 decoration-[#DFC590]">Oder schriftlich: Bewertung anfordern</Link>
          </div>
          <p className="text-sm text-[#647064] mt-5">Mo–Fr 9–17 Uhr · Rückruf auf Wunsch auch abends</p>
        </div>
      </div>
    </div>
  </section>
);

export const BuyerPathSection = () => (
  <section className="hv-buyers bg-[#EEE9DC] py-24 lg:py-32" data-testid="homepage-buyer-path">
    <div className="container mx-auto px-4">
      <SectionHeading kicker="Auch für Käufer" title="Käufer und Verkäufer finden bei uns zusammen – oft, bevor etwas inseriert wird." />
      <div className="grid md:grid-cols-2 gap-5">
        <Link to="/kaeufer-radar" data-testid="buyer-radar-card" className="hv-card hv-card-light group bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] p-8 lg:p-10">
          <Radar className="w-7 h-7 text-[#1C432F] mb-6" aria-hidden="true" />
          <h3 className="hv-serif text-[28px] leading-tight text-[#19392B] mb-4">Gibt es schon Käufer für Ihre Fläche?</h3>
          <p className="text-[#647064] leading-relaxed mb-6">Der Käufer-Radar zeigt, ob in Ihrem Landkreis vorgemerkte Interessenten auf genau Ihre Flächenart warten – anonym und in einer Minute.</p>
          <span className="inline-flex items-center gap-2 text-[#19392B] font-medium">Käufer-Radar starten <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
        </Link>
        <Link to="/suchprofil" data-testid="buyer-profile-card" className="hv-card hv-card-light group bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] p-8 lg:p-10">
          <Users className="w-7 h-7 text-[#1C432F] mb-6" aria-hidden="true" />
          <h3 className="hv-serif text-[28px] leading-tight text-[#19392B] mb-4">Sie suchen Flächen zum Kauf?</h3>
          <p className="text-[#647064] leading-relaxed mb-6">Legen Sie ein Suchprofil an. Passende Angebote erreichen Sie, bevor sie öffentlich werden – viele Flächen vermitteln wir ohne Inserat.</p>
          <span className="inline-flex items-center gap-2 text-[#19392B] font-medium">Suchprofil anlegen <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
        </Link>
      </div>
    </div>
  </section>
);

export const KnowledgeSection = () => {
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    axios.get(`${API}/api/getautoseo-feed?slim=1&limit=3`).then((r) => setArticles((r.data.items || []).slice(0, 3))).catch(() => setArticles([]));
  }, []);
  return (
    <section className="hv-knowledge bg-[#FFFEFA] py-24 lg:py-32" data-testid="homepage-knowledge">
      <div className="container mx-auto px-4 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="bg-[#EEE9DC] rounded-[4px] p-8 lg:p-10 h-full flex flex-col">
            <Scale className="w-7 h-7 text-[#1C432F] mb-5" aria-hidden="true" />
            <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.14em] text-[#647064] mb-3">Erbe & Hofnachfolge</p>
            <h3 className="hv-serif text-[28px] leading-tight text-[#19392B] mb-3">Behalten, verkaufen oder aufteilen?</h3>
            <p className="text-[#647064] leading-relaxed mb-6">Der Nachlass-Planer ordnet Ihre Familiensituation ehrlich ein – in fünf Minuten, ohne Verkaufsdruck. Manchmal ist Behalten die richtige Antwort.</p>
            <button type="button" onClick={() => { trackEvent(analyticsEvents.TOOL_CLICK, { tool: 'erben_rechner', location: 'home' }); navigate('/nachlass-planer'); }} data-testid="nachlass-planer-cta" className="hv-btn-secondary mt-auto inline-flex items-center justify-center gap-2 h-[48px] px-5 rounded-[4px] border border-[#1C432F] text-[#19392B] font-semibold hover:bg-[#FFFEFA]">Nachlass-Planer öffnen <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="flex items-end justify-between gap-6 mb-6">
            <div>
              <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.14em] text-[#647064] mb-3">Ratgeber</p>
              <h3 className="hv-serif text-[28px] leading-tight text-[#19392B]">Wissen, das vor dem Verkauf hilft.</h3>
            </div>
            <Link to="/ratgeber" data-testid="ratgeber-all-link" className="hv-link inline-flex items-center gap-2 text-[#19392B] font-medium underline underline-offset-4 whitespace-nowrap"><BookOpen className="w-4 h-4" aria-hidden="true" />Alle Artikel</Link>
          </div>
          <ul className="divide-y divide-[#DEDFD3] border-y border-[#DEDFD3]" data-testid="ratgeber-list">
            {articles.map((a) => (
              <li key={a.slug || a.id}>
                <Link to={`/ratgeber/${a.slug || a.id}`} className="hv-article group flex items-start justify-between gap-6 py-5">
                  <div>
                    <p className="text-sm text-[#647064] mb-1">{a.date_published ? new Date(a.date_published).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Ratgeber'}</p>
                    <h4 className="text-lg font-medium text-[#19392B] leading-snug">{a.title}</h4>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#647064] mt-2 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </li>
            ))}
            {articles.length === 0 && <li className="py-5 text-[#647064]">Aktuelle Artikel finden Sie im Ratgeber.</li>}
          </ul>
        </div>
      </div>
    </section>
  );
};

export const FaqSection = () => (
  <section id="faq-section" className="hv-faq bg-[#F8F6F0] py-24 lg:py-32" data-testid="homepage-faq">
    <div className="container mx-auto px-4 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <SectionHeading kicker="Häufige Fragen" title="Was Eigentümer uns zuerst fragen." text="Kurz und ohne Juristendeutsch. Alles Weitere klären wir im Gespräch." />
      </div>
      <div className="lg:col-span-8">
        <Accordion type="single" collapsible className="divide-y divide-[#DEDFD3] border-y border-[#DEDFD3]">
          {homeFaq.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-0">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} onClick={() => trackEvent(analyticsEvents.FAQ_EXPAND, { question_index: i })} className="hv-faq-trigger py-5 text-left text-lg font-medium text-[#19392B] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="pb-6 text-[#647064] leading-relaxed text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

export const FinalCtaSection = () => (
  <section className="hv-final hv-dark relative overflow-hidden bg-[#0F2E22] py-28 lg:py-40" data-testid="homepage-final-cta">
    <div className="absolute inset-0" aria-hidden="true">
      <img src={AERIAL_LANDSCAPE} alt="" loading="lazy" className="w-full h-full object-cover" />
      <div className="hv-final-fade absolute inset-0" />
    </div>
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-[820px]">
        <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.14em] text-[#DFC590] mb-4">Der nächste Schritt</p>
        <h2 className="hv-statement text-[#F8F6F0] mb-6">Lassen Sie Ihre Fläche einordnen, <em className="hv-accent">bevor</em> Sie entscheiden.</h2>
        <p className="text-lg text-[#C9D3C8] leading-relaxed mb-8">Kostenlos, unverbindlich und diskret. Sie bekommen eine ehrliche Ersteinschätzung – und entscheiden danach in Ruhe.</p>
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/bewertung" data-testid="final-cta-button" className="hv-btn-accent inline-flex items-center justify-center h-[56px] px-8 rounded-[4px] bg-[#DFC590] text-[#19392B] font-semibold hover:bg-[#E8D3A9]">Kostenlose Bewertung starten</Link>
          <a href={contactInfo.phoneLink} onClick={() => trackEvent(analyticsEvents.PHONE_CLICK, { location: 'final_cta' })} data-testid="final-cta-phone" className="inline-flex items-center gap-2 h-[56px] px-4 text-[#F8F6F0] font-medium"><Phone className="w-4 h-4" aria-hidden="true" />{contactInfo.phone}</a>
          <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: 'whatsapp', location: 'final_cta' })} data-testid="final-cta-whatsapp" className="inline-flex items-center gap-2 h-[56px] px-4 text-[#F8F6F0] font-medium"><MessageCircle className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />Per WhatsApp schreiben</a>
        </div>
      </div>
    </div>
  </section>
);
